import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Paper, Chip, Button, Grid, Skeleton, Divider, IconButton, Dialog, Tabs, Tab, Menu, MenuItem } from '@mui/material';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import SEO from '../components/SEO';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShareIcon from '@mui/icons-material/Share';

import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import CollectionsIcon from '@mui/icons-material/Collections';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DirectionsIcon from '@mui/icons-material/Directions';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { useLanguage } from '../context/LanguageContext';
import type { BusinessAd } from '../components/BusinessPromotions';

// Convert business name to URL-friendly slug
function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Extract YouTube video ID from various URL formats
function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/,
    /youtube\.com\/shorts\/([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

const categoryColors: Record<string, string> = {
  Shopping: '#e91e63',
  Restaurant: '#ff9800',
  'Pooja Items': '#4caf50',
  Jewellery: '#9c27b0',
  Hotel: '#2196f3',
  Travel: '#00bcd4',
  Services: '#607d8b',
};

export default function BusinessDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [ad, setAd] = useState<BusinessAd | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [mediaFilter, setMediaFilter] = useState<'all' | 'photos' | 'videos'>('all');
  const [lightboxType, setLightboxType] = useState<'image' | 'video'>('image');
  const [heroSlide, setHeroSlide] = useState(0);
  const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(null);
  const heroTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { translateLanguage, setTranslateLanguage, availableLanguages } = useLanguage();

  // Build banner slider images array from gallery images + banner
  const heroImages = useMemo(() => {
    if (!ad) return [];
    const imgs = [ad.bannerUrl];
    (ad.galleryImages || []).forEach((img) => {
      if (img !== ad.bannerUrl) imgs.push(img);
    });
    return imgs;
  }, [ad]);

  const goToSlide = useCallback((index: number) => {
    setHeroSlide(index);
  }, []);

  const nextSlide = useCallback(() => {
    setHeroSlide((prev) => (prev + 1) % heroImages.length);
  }, [heroImages.length]);

  const prevSlide = useCallback(() => {
    setHeroSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  }, [heroImages.length]);

  // Auto-play hero slider
  useEffect(() => {
    if (heroImages.length <= 1) return;
    heroTimerRef.current = setInterval(nextSlide, 4000);
    return () => {
      if (heroTimerRef.current) clearInterval(heroTimerRef.current);
    };
  }, [heroImages.length, nextSlide]);

  useEffect(() => {
    const fetchAd = async () => {
      if (!id) return;
      try {
        // Direct document lookup by slug (document ID = slug)
        const docRef = doc(db, 'business_promotions', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().isActive !== false) {
          setAd({ id: docSnap.id, ...docSnap.data() } as BusinessAd);
        } else {
          // Fallback: query by slug field
          const slugQuery = query(
            collection(db, 'business_promotions'),
            where('slug', '==', id)
          );
          const slugSnap = await getDocs(slugQuery);
          if (!slugSnap.empty) {
            const d = slugSnap.docs[0];
            const data = d.data();
            if (data.isActive !== false) {
              setAd({ id: d.id, ...data } as BusinessAd);
            }
          }
        }
      } catch (err) {
        console.error('Error loading business:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
  }, [id]);

  // Increment view count when business is loaded
  useEffect(() => {
    if (!ad) return;
    const docRef = doc(db, 'business_promotions', ad.id);
    updateDoc(docRef, { viewCount: increment(1) }).catch(() => {});
  }, [ad?.id]);

  const catColor = categoryColors[ad?.category || ''] || '#9c27b0';

  // Build combined media items array (must be before early returns — hooks rule)
  const allMedia = useMemo(() => {
    if (!ad) return [];
    const items: Array<{ type: 'image' | 'video'; src: string; videoId?: string }> = [];
    (ad.galleryImages || []).forEach((src) => items.push({ type: 'image', src }));
    (ad.youtubeVideos || []).forEach((url) => {
      const vid = getYouTubeId(url);
      if (vid) items.push({ type: 'video', src: url, videoId: vid });
    });
    return items;
  }, [ad]);

  const filteredMedia = useMemo(() => {
    if (mediaFilter === 'photos') return allMedia.filter((m) => m.type === 'image');
    if (mediaFilter === 'videos') return allMedia.filter((m) => m.type === 'video');
    return allMedia;
  }, [allMedia, mediaFilter]);

  const photoCount = allMedia.filter((m) => m.type === 'image').length;
  const videoCount = allMedia.filter((m) => m.type === 'video').length;

  // Social links helper
  const socialPlatforms = useMemo(() => [
    { key: 'facebook' as const, icon: <FacebookIcon />, label: 'Facebook', color: '#1877F2' },
    { key: 'instagram' as const, icon: <InstagramIcon />, label: 'Instagram', color: '#E4405F' },
    { key: 'youtube' as const, icon: <YouTubeIcon />, label: 'YouTube', color: '#FF0000' },
    { key: 'twitter' as const, icon: <XIcon />, label: 'X (Twitter)', color: '#000' },
  ], []);

  if (loading) {
    return (
      <Box sx={{
        position: 'fixed', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(160deg, #0f1923 0%, #1a2d3d 40%, #0d1b25 100%)',
        zIndex: 9999,
        overflow: 'hidden',
        animation: 'bdFadeIn 0.4s ease-out',
        '@keyframes bdFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
      }}>
        {/* Ambient glow */}
        <Box sx={{
          position: 'absolute',
          width: 350, height: 350, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(56,189,248,0.08) 0%, rgba(56,189,248,0.03) 40%, transparent 65%)',
          animation: 'bdGlow 3s ease-in-out infinite',
          '@keyframes bdGlow': {
            '0%, 100%': { transform: 'scale(0.9)', opacity: 0.5 },
            '50%': { transform: 'scale(1.15)', opacity: 1 },
          },
        }} />

        {/* Floating dots */}
        {[...Array(8)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: 3 + (i % 3),
              height: 3 + (i % 3),
              borderRadius: '50%',
              bgcolor: `rgba(56,189,248,${0.15 + (i % 4) * 0.05})`,
              left: `${10 + (i * 11) % 80}%`,
              top: `${15 + (i * 14) % 70}%`,
              animation: `bdFloat ${3 + (i % 3)}s ease-in-out infinite`,
              animationDelay: `${i * 0.25}s`,
              '@keyframes bdFloat': {
                '0%, 100%': { transform: 'translateY(0)', opacity: 0.3 },
                '50%': { transform: 'translateY(-18px)', opacity: 0.7 },
              },
            }}
          />
        ))}

        {/* Main content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 2 }}>
          {/* Icon with rings */}
          <Box sx={{ position: 'relative', mb: 4 }}>
            {/* Outer ring */}
            <Box sx={{
              position: 'absolute', top: -16, left: -16, right: -16, bottom: -16,
              borderRadius: '50%',
              border: '1.5px solid rgba(56,189,248,0.15)',
              animation: 'bdSpin 10s linear infinite',
              '@keyframes bdSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
              '&::before': {
                content: '""', position: 'absolute',
                top: -3, left: '50%', transform: 'translateX(-50%)',
                width: 5, height: 5, borderRadius: '50%',
                bgcolor: '#38bdf8', boxShadow: '0 0 10px #38bdf8',
              },
            }} />
            {/* Inner dashed ring */}
            <Box sx={{
              position: 'absolute', top: -4, left: -4, right: -4, bottom: -4,
              borderRadius: '50%',
              border: '1px dashed rgba(56,189,248,0.2)',
              animation: 'bdSpinRev 7s linear infinite',
              '@keyframes bdSpinRev': { from: { transform: 'rotate(360deg)' }, to: { transform: 'rotate(0deg)' } },
            }} />
            {/* Center icon */}
            <Box sx={{
              width: 80, height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(145deg, rgba(56,189,248,0.2), rgba(56,189,248,0.05))',
              border: '2px solid rgba(56,189,248,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 40px rgba(56,189,248,0.15), inset 0 0 20px rgba(56,189,248,0.08)',
              animation: 'bdBreathe 3s ease-in-out infinite',
              '@keyframes bdBreathe': {
                '0%, 100%': { boxShadow: '0 0 40px rgba(56,189,248,0.15), inset 0 0 20px rgba(56,189,248,0.08)' },
                '50%': { boxShadow: '0 0 60px rgba(56,189,248,0.25), inset 0 0 30px rgba(56,189,248,0.15)' },
              },
            }}>
              <StorefrontIcon sx={{ fontSize: 38, color: '#38bdf8', filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.4))' }} />
            </Box>
          </Box>

          {/* Title */}
          <Typography sx={{
            fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.25rem' },
            letterSpacing: 0.5,
            background: 'linear-gradient(135deg, #fff 30%, #38bdf8 70%, #7dd3fc 100%)',
            backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textAlign: 'center', mb: 0.8,
            animation: 'bdReveal 0.8s 0.3s cubic-bezier(0.16,1,0.3,1) both',
            '@keyframes bdReveal': {
              from: { opacity: 0, transform: 'translateY(12px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}>
            {id ? id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Loading Business Details'}
          </Typography>

          <Typography sx={{
            fontSize: '0.62rem', color: 'rgba(255,255,255,0.3)',
            fontWeight: 600, letterSpacing: 4, textTransform: 'uppercase', mb: 4,
            animation: 'bdReveal 0.8s 0.5s cubic-bezier(0.16,1,0.3,1) both',
          }}>
            Loading
          </Typography>

          {/* Loading bar */}
          <Box sx={{
            width: 140, height: 2, borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.06)',
            overflow: 'hidden', position: 'relative',
            animation: 'bdReveal 0.8s 0.7s cubic-bezier(0.16,1,0.3,1) both',
          }}>
            <Box sx={{
              position: 'absolute', top: 0, left: 0,
              height: '100%', width: '40%',
              borderRadius: 2,
              background: 'linear-gradient(90deg, transparent, #38bdf8, transparent)',
              animation: 'bdBar 1.5s ease-in-out infinite',
              '@keyframes bdBar': { '0%': { left: '-40%' }, '100%': { left: '100%' } },
            }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (!ad) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <StorefrontIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#444', mb: 1 }}>Business Not Found</Typography>
          <Typography sx={{ color: '#888' }}>This promotion may have expired or been removed.</Typography>
        </Container>
      </Box>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: ad.businessName, text: ad.tagline, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleWhatsApp = () => {
    if (!ad?.whatsapp) return;
    const num = ad.whatsapp.replace(/[^0-9]/g, '');
    const msg = encodeURIComponent(`Hi, I found your business "${ad.businessName}" on Kottayil Temple website. I'd like to inquire about your services.`);
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <SEO
        title={`${ad.businessName} — ${ad.category} | Promoted on Kottayil Temple`}
        description={`${ad.tagline}. ${ad.description.substring(0, 120)}...`}
        keywords={`${ad.businessName}, ${ad.category}, Muchukunnu, Kozhikode, local business, ${ad.offer || ''}`}
      />

      {/* ====== BUSINESS-FOCUSED HEADER ====== */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f1923 0%, #162736 100%)',
        borderBottom: '2px solid rgba(56,189,248,0.12)',
        py: { xs: 0.8, md: 1.2 }, px: { xs: 1.5, sm: 2, md: 4 },
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        backdropFilter: 'blur(14px)',
        gap: { xs: 0.5, md: 1 },
        overflow: 'hidden',
      }}>
        {/* Left — Business branding */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, minWidth: 0, flex: 1, overflow: 'hidden' }}>
          {ad.logoUrl ? (
            <Box
              component="img"
              src={ad.logoUrl}
              alt={ad.businessName}
              sx={{
                width: 36, height: 36, borderRadius: 1.5,
                objectFit: 'cover',
                border: '1.5px solid rgba(255,255,255,0.12)',
                flexShrink: 0,
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              background: 'linear-gradient(135deg, rgba(56,189,248,0.2), rgba(56,189,248,0.08))',
              border: '1.5px solid rgba(56,189,248,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <StorefrontIcon sx={{ fontSize: 20, color: '#38bdf8' }} />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
              color: '#fff', fontWeight: 800, fontSize: { xs: 13, md: 15 },
              lineHeight: 1.2, letterSpacing: 0.2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {ad.businessName}
            </Typography>
            <Typography sx={{
              color: 'rgba(255,255,255,0.4)', fontSize: { xs: 9, md: 10 },
              fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {ad.category}{ad.address ? ` · ${ad.address.split(',')[0]}` : ''}
            </Typography>
          </Box>
        </Box>

        {/* Right — Actions + Temple link */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 }, flexShrink: 0 }}>
          {ad.phone && (
            <IconButton
              component="a" href={`tel:${ad.phone}`}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                display: { xs: 'none', sm: 'flex' },
                '&:hover': { color: '#38bdf8', background: 'rgba(56,189,248,0.1)' },
              }}
            >
              <PhoneIcon sx={{ fontSize: 17 }} />
            </IconButton>
          )}
          <IconButton
            onClick={handleShare}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#38bdf8', background: 'rgba(56,189,248,0.1)' } }}
          >
            <ShareIcon sx={{ fontSize: 17 }} />
          </IconButton>
          {/* Language picker */}
          {(() => {
            const activeLang = availableLanguages.find(l => l.code === translateLanguage) || availableLanguages[0];
            return (
          <>
          <Box
            className="notranslate"
            onClick={(e: React.MouseEvent<HTMLElement>) => setLangMenuAnchor(e.currentTarget)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              cursor: 'pointer', px: { xs: 0.7, md: 1 }, py: 0.35,
              borderRadius: '10px',
              bgcolor: Boolean(langMenuAnchor) ? 'rgba(56,189,248,0.12)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${Boolean(langMenuAnchor) ? 'rgba(56,189,248,0.3)' : 'rgba(255,255,255,0.08)'}`,
              '&:hover': { bgcolor: 'rgba(56,189,248,0.1)', borderColor: 'rgba(56,189,248,0.25)' },
              transition: 'all 200ms',
            }}>
            <TranslateIcon sx={{ fontSize: 14, color: '#38bdf8', opacity: 0.85 }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff', lineHeight: 1, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
              {activeLang.nativeLabel}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', transition: 'transform 200ms', transform: Boolean(langMenuAnchor) ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </Box>
          <Menu anchorEl={langMenuAnchor} open={Boolean(langMenuAnchor)} onClose={() => setLangMenuAnchor(null)}
            TransitionProps={{ timeout: 200 }}
            PaperProps={{ sx: {
              bgcolor: 'rgba(15,25,35,0.98)',
              backdropFilter: 'blur(32px)',
              border: '1px solid rgba(56,189,248,0.15)',
              borderRadius: '14px',
              mt: 1.5,
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              minWidth: 240, maxHeight: 440, overflowY: 'auto',
              p: 0,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(56,189,248,0.3)', borderRadius: 4 },
            } }}>
            <Box sx={{ px: 2, pt: 1.3, pb: 0.8, display: 'flex', alignItems: 'center', gap: 0.8, borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
              <TranslateIcon sx={{ fontSize: 16, color: '#38bdf8' }} />
              <Typography className="notranslate" sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>
                Select Language
              </Typography>
            </Box>
            <Box sx={{ p: 0.5 }}>
            {availableLanguages.map((lang) => {
              const isLangActive = translateLanguage === lang.code;
              return (
              <MenuItem key={lang.code} className="notranslate"
                onClick={() => { setTranslateLanguage(lang.code); setLangMenuAnchor(null); }}
                sx={{
                  color: '#fff', py: 0.7, px: 1.2, fontSize: '0.82rem',
                  borderRadius: '8px', mb: 0.2,
                  display: 'flex', alignItems: 'center', gap: 1,
                  bgcolor: isLangActive ? 'rgba(56,189,248,0.12)' : 'transparent',
                  border: isLangActive ? '1px solid rgba(56,189,248,0.25)' : '1px solid transparent',
                  '&:hover': { bgcolor: isLangActive ? 'rgba(56,189,248,0.18)' : 'rgba(255,255,255,0.05)' },
                  transition: 'all 150ms',
                }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: '6px', flexShrink: 0,
                  background: isLangActive ? 'linear-gradient(135deg, #38bdf8, #0ea5e9)' : 'rgba(255,255,255,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: isLangActive ? 'none' : '1px solid rgba(255,255,255,0.06)',
                }}>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: isLangActive ? '#fff' : 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {lang.code}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: isLangActive ? 700 : 500, color: isLangActive ? '#fff' : 'rgba(255,255,255,0.8)', lineHeight: 1.3 }}>
                    {lang.nativeLabel}
                  </Typography>
                  {lang.code !== 'en' && (
                    <Typography sx={{ fontSize: '0.58rem', color: isLangActive ? '#38bdf8' : 'rgba(255,255,255,0.3)', lineHeight: 1.2, fontWeight: 500 }}>
                      {lang.label}
                    </Typography>
                  )}
                </Box>
                {isLangActive && (
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: 'rgba(56,189,248,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckIcon sx={{ fontSize: 12, color: '#38bdf8' }} />
                  </Box>
                )}
              </MenuItem>
              );
            })}
            </Box>
          </Menu>
          </>
            );
          })()}
          {/* Temple link — stacked micro-card */}
          <Box
            component={RouterLink} to="/"
            sx={{
              textDecoration: 'none',
              ml: 1,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              px: 1.2, py: 0.4,
              borderLeft: '2px solid rgba(212,175,55,0.2)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 300ms cubic-bezier(0.4,0,0.2,1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0,
                width: '100%', height: '100%',
                background: 'linear-gradient(90deg, rgba(212,175,55,0.12), transparent)',
                transform: 'translateX(-100%)',
                transition: 'transform 400ms cubic-bezier(0.4,0,0.2,1)',
              },
              '&:hover::before': { transform: 'translateX(0)' },
              '&:hover': {
                borderLeftColor: '#d4af37',
                borderLeftWidth: '3px',
                pl: 1.1,
              },
              '&:hover .tp-name': {
                color: '#d4af37',
                transform: 'translateX(2px)',
                textShadow: '0 0 12px rgba(212,175,55,0.3)',
              },
              '&:hover .tp-sub': {
                color: 'rgba(255,255,255,0.55)',
                transform: 'translateX(2px)',
                letterSpacing: 3,
              },
              '&:active': {
                transform: 'scale(0.96)',
              },
            }}
          >
            <Typography className="tp-name" sx={{
              color: 'rgba(255,255,255,0.55)',
              fontSize: 11, fontWeight: 800, lineHeight: 1.1,
              letterSpacing: 0.2,
              mb: 0.4,
              position: 'relative', zIndex: 1,
              transition: 'all 300ms cubic-bezier(0.4,0,0.2,1)',
            }}>
              Kottakovilakam
            </Typography>
            <Typography className="tp-sub" sx={{
              color: 'rgba(255,255,255,0.25)',
              fontSize: 7.5, fontWeight: 700, letterSpacing: 2,
              textTransform: 'uppercase', lineHeight: 1,
              position: 'relative', zIndex: 1,
              transition: 'all 400ms cubic-bezier(0.4,0,0.2,1)',
            }}>
              Temple · Muchukunnu
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ====== FULL-BLEED HERO BANNER — Image Slider ====== */}
      <Box sx={{
        position: 'relative',
        height: { xs: 400, sm: 460, md: 540 },
        overflow: 'hidden',
      }}>
        {/* Sliding background images */}
        {heroImages.map((img, idx) => (
          <Box key={idx} sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${img})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            filter: 'brightness(0.45)',
            opacity: heroSlide === idx ? 1 : 0,
            transform: heroSlide === idx ? 'scale(1.02)' : 'scale(1.08)',
            transition: 'opacity 1s ease, transform 6s ease',
          }} />
        ))}
        {/* Deep gradient overlay */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(180deg, rgba(26,15,46,0.4) 0%, rgba(26,15,46,0.2) 30%, rgba(26,15,46,0.6) 70%, rgba(26,15,46,0.95) 100%)`,
          zIndex: 1,
        }} />
        {/* Colored accent glow at bottom */}
        <Box sx={{
          position: 'absolute', bottom: -2, left: 0, right: 0, height: 4,
          background: `linear-gradient(90deg, ${catColor}, ${catColor}80, transparent)`,
          zIndex: 3,
        }} />

        {/* Slider navigation arrows */}
        {heroImages.length > 1 && (
          <>
            <IconButton
              onClick={() => { prevSlide(); if (heroTimerRef.current) { clearInterval(heroTimerRef.current); heroTimerRef.current = setInterval(nextSlide, 4000); } }}
              sx={{
                position: 'absolute', left: { xs: 8, md: 20 }, top: '50%', transform: 'translateY(-50%)',
                zIndex: 4, color: '#fff', bgcolor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)',
                width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 },
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
            </IconButton>
            <IconButton
              onClick={() => { nextSlide(); if (heroTimerRef.current) { clearInterval(heroTimerRef.current); heroTimerRef.current = setInterval(nextSlide, 4000); } }}
              sx={{
                position: 'absolute', right: { xs: 8, md: 20 }, top: '50%', transform: 'translateY(-50%)',
                zIndex: 4, color: '#fff', bgcolor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)',
                width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 },
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
            </IconButton>
          </>
        )}

        {/* Hero content */}
        <Container maxWidth="lg" sx={{
          position: 'relative', zIndex: 2, height: '100%',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          pb: { xs: 3, md: 5 },
          px: { xs: 2, sm: 3, md: 4 },
        }}>
          {/* Badges row */}
          <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 14, color: '#4caf50 !important' }} />}
              label="Verified Business"
              size="small"
              sx={{
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)',
                border: '1px solid rgba(76,175,80,0.3)',
                color: '#a5d6a7', fontWeight: 700, fontSize: 11,
              }}
            />
            <Chip
              icon={<StorefrontIcon sx={{ fontSize: 14, color: `${catColor} !important` }} />}
              label={ad.category}
              size="small"
              sx={{
                background: `${catColor}25`, backdropFilter: 'blur(10px)',
                border: `1px solid ${catColor}40`,
                color: catColor, fontWeight: 700, fontSize: 12,
              }}
            />
            {ad.offer && (
              <Chip
                icon={<LocalOfferIcon sx={{ fontSize: 14, color: '#ffd54f !important' }} />}
                label={ad.offer}
                size="small"
                sx={{
                  background: 'rgba(255,213,79,0.15)', backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,213,79,0.3)',
                  color: '#ffd54f', fontWeight: 700, fontSize: 12,
                }}
              />
            )}
          </Box>

          {/* Business name */}
          <Typography variant="h3" sx={{
            fontWeight: 900, color: '#fff', mb: 0.8,
            fontSize: { xs: '1.5rem', sm: '2.2rem', md: '3rem' },
            textShadow: '0 4px 20px rgba(0,0,0,0.5)',
            lineHeight: 1.15,
            wordBreak: 'break-word',
          }}>
            {ad.businessName}
          </Typography>

          {/* Tagline */}
          <Typography sx={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: { xs: '0.88rem', sm: '1rem', md: '1.2rem' },
            maxWidth: 650, lineHeight: 1.6,
            textShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}>
            {ad.tagline}
          </Typography>

          {/* Quick location line */}
          {ad.address && (
            <Box
              component="a"
              href={ad.mapUrl || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ad.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, mt: 1, textDecoration: 'none', '&:hover .addr-text': { textDecoration: 'underline' } }}
            >
              <LocationOnIcon sx={{ fontSize: 16, color: catColor, mt: 0.3, flexShrink: 0 }} />
              <Typography className="addr-text" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: '0.8rem', md: '0.9rem' }, lineHeight: 1.4 }}>
                {ad.address}
              </Typography>
              <DirectionsIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', ml: 0.3, flexShrink: 0, mt: 0.3 }} />
            </Box>
          )}

          {/* Dot indicators */}
          {heroImages.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.8, mt: 2.5 }}>
              {heroImages.map((_, idx) => (
                <Box
                  key={idx}
                  onClick={() => { goToSlide(idx); if (heroTimerRef.current) { clearInterval(heroTimerRef.current); heroTimerRef.current = setInterval(nextSlide, 4000); } }}
                  sx={{
                    width: heroSlide === idx ? 28 : 8, height: 8,
                    borderRadius: 4,
                    background: heroSlide === idx ? catColor : 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    transition: 'all 0.4s ease',
                    '&:hover': { background: heroSlide === idx ? catColor : 'rgba(255,255,255,0.7)' },
                  }}
                />
              ))}
            </Box>
          )}
        </Container>
      </Box>

      {/* ====== MAIN CONTENT ====== */}
      <Box sx={{ background: '#faf9fc', minHeight: '60vh' }}>

        {/* ── HIGHLIGHTS / WHY CHOOSE US STRIP ── */}
        {ad.highlights && ad.highlights.length > 0 && (
          <Box sx={{ background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                {ad.highlights.map((h, idx) => (
                  <Grid item xs={6} sm={3} key={idx}>
                    <Box sx={{ textAlign: 'center', py: { xs: 0.5, md: 1 } }}>
                      <Box sx={{
                        width: { xs: 42, md: 52 }, height: { xs: 42, md: 52 }, mx: 'auto', mb: 1, borderRadius: '50%',
                        background: `${catColor}10`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 26, color: catColor }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: { xs: '0.82rem', md: '0.95rem' }, mb: 0.3 }}>
                        {h.title}
                      </Typography>
                      <Typography sx={{ color: '#888', fontSize: '0.8rem', lineHeight: 1.5 }}>
                        {h.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* ── PRODUCT / SERVICE CATALOG ── */}
        {ad.products && ad.products.length > 0 && (
          <Box sx={{ background: '#fff', py: { xs: 3, md: 6 }, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
              <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 4 } }}>
                <Typography sx={{ fontSize: { xs: 11, md: 12 }, fontWeight: 700, color: catColor, textTransform: 'uppercase', letterSpacing: 2, mb: 0.8 }}>
                  Our Collection
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#1a1a2e', mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Products & Services
                </Typography>
                <Box sx={{ width: 60, height: 3, background: `linear-gradient(90deg, ${catColor}, ${catColor}60)`, mx: 'auto', borderRadius: 2 }} />
              </Box>

              <Grid container spacing={{ xs: 1.5, md: 2.5 }}>
                {ad.products.map((product, idx) => (
                  <Grid item xs={6} sm={4} md={4} key={idx}>
                    <Paper elevation={0} sx={{
                      borderRadius: { xs: 2, md: 3 }, overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.06)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      display: 'flex', flexDirection: 'column',
                      '&:hover': { boxShadow: '0 12px 40px rgba(0,0,0,0.1)', transform: 'translateY(-4px)' },
                      '&:hover .prod-img': { transform: 'scale(1.08)' },
                    }}>
                      <Box sx={{
                        height: { xs: 140, sm: 170, md: 200 }, overflow: 'hidden',
                        position: 'relative',
                      }}>
                        <Box
                          className="prod-img"
                          component="img" src={product.image}
                          alt={product.name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                        />
                        {product.price && (
                          <Chip
                            label={product.price}
                            size="small"
                            sx={{
                              position: 'absolute', bottom: 8, right: 8,
                              background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
                              color: '#fff', fontWeight: 700, fontSize: 11,
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ p: { xs: 1.2, sm: 1.5, md: 2 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: { xs: '0.78rem', sm: '0.85rem', md: '0.95rem' }, mb: 0.5, lineHeight: 1.3 }}>
                          {product.name}
                        </Typography>
                        {product.description && (
                          <Typography sx={{ color: '#888', fontSize: { xs: '0.68rem', sm: '0.72rem', md: '0.8rem' }, lineHeight: 1.5, flex: 1 }}>
                            {product.description}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Inquiry CTA below products */}
              {ad.whatsapp && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Button
                    onClick={handleWhatsApp}
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      background: '#25D366', color: '#fff',
                      borderRadius: 3, textTransform: 'none',
                      fontWeight: 700, fontSize: '1rem',
                      px: 4, py: 1.3,
                      boxShadow: '0 6px 20px rgba(37,211,102,0.3)',
                      '&:hover': { background: '#1ebe5d', boxShadow: '0 8px 28px rgba(37,211,102,0.45)' },
                    }}
                  >
                    Inquire About Products
                  </Button>
                </Box>
              )}
            </Container>
          </Box>
        )}

        {/* ── TWO-COLUMN: About/Gallery/Map + Contact/Social sidebar ── */}
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 2.5, md: 4 }}>

            {/* Left column: About + Gallery + Offer + Map */}
            <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
              {/* About section */}
              <Paper elevation={0} sx={{
                p: { xs: 2.5, sm: 3, md: 4 }, borderRadius: { xs: 3, md: 4 }, mb: { xs: 2, md: 3 },
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.06)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 4, height: 28, background: `linear-gradient(180deg, ${catColor}, ${catColor}80)`, borderRadius: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                    About This Business
                  </Typography>
                </Box>
                <Typography sx={{
                  color: '#555', fontSize: { xs: '0.95rem', md: '1.05rem' }, lineHeight: 1.9,
                  whiteSpace: 'pre-line',
                }}>
                  {ad.description}
                </Typography>

                {/* Quick info chips row */}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 3, pt: 2.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  {ad.established && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.6, borderRadius: 2.5, background: '#f5f5f5' }}>
                      <CalendarMonthIcon sx={{ fontSize: 16, color: '#888' }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#666' }}>Est. {ad.established}</Typography>
                    </Box>
                  )}
                  {ad.openingHours && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.6, borderRadius: 2.5, background: '#f5f5f5' }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: '#888' }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#666' }}>{ad.openingHours}</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* ====== MEDIA GALLERY — Photos & Videos with Filters ====== */}
              {allMedia.length > 0 && (
                <Paper elevation={0} sx={{
                  p: { xs: 2, sm: 3, md: 4 }, borderRadius: { xs: 3, md: 4 }, mb: { xs: 2, md: 3 },
                  background: '#fff',
                  border: '1px solid rgba(0,0,0,0.06)',
                  overflow: 'hidden',
                }}>
                  {/* Header row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ width: 4, height: 28, background: `linear-gradient(180deg, ${catColor}, ${catColor}80)`, borderRadius: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e' }}>
                      Media Gallery
                    </Typography>
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.8 }}>
                      {photoCount > 0 && (
                        <Chip
                          icon={<PhotoLibraryIcon sx={{ fontSize: 13, color: `${catColor} !important` }} />}
                          label={`${photoCount} Photos`} size="small"
                          sx={{ background: `${catColor}10`, color: catColor, fontWeight: 700, fontSize: 11 }}
                        />
                      )}
                      {videoCount > 0 && (
                        <Chip
                          icon={<VideocamIcon sx={{ fontSize: 13, color: '#FF0000 !important' }} />}
                          label={`${videoCount} Videos`} size="small"
                          sx={{ background: 'rgba(255,0,0,0.08)', color: '#d32f2f', fontWeight: 700, fontSize: 11 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Filter tabs — only show if both types exist */}
                  {photoCount > 0 && videoCount > 0 && (
                    <Tabs
                      value={mediaFilter}
                      onChange={(_, v) => setMediaFilter(v)}
                      sx={{
                        minHeight: 38, mb: 2.5,
                        '& .MuiTab-root': {
                          minHeight: 36, textTransform: 'none', fontWeight: 700, fontSize: 13,
                          borderRadius: 2.5, mr: 1, px: 2, color: '#888',
                          '&.Mui-selected': { color: catColor, background: `${catColor}10` },
                        },
                        '& .MuiTabs-indicator': { background: catColor, height: 2.5, borderRadius: 2 },
                      }}
                    >
                      <Tab value="all" icon={<CollectionsIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`All (${allMedia.length})`} />
                      <Tab value="photos" icon={<PhotoLibraryIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Photos (${photoCount})`} />
                      <Tab value="videos" icon={<VideocamIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Videos (${videoCount})`} />
                    </Tabs>
                  )}

                  {/* Media grid */}
                  <Grid container spacing={1.5}>
                    {filteredMedia.map((item, idx) => (
                      <Grid item xs={6} sm={idx === 0 ? 8 : 4} key={`${item.type}-${idx}`}>
                        {item.type === 'image' ? (
                          <Box
                            onClick={() => { setLightboxIndex(idx); setLightboxType('image'); setLightboxOpen(true); }}
                            sx={{
                              borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                              height: { xs: idx === 0 ? 200 : 140, md: idx === 0 ? 260 : 170 },
                              position: 'relative',
                              '&:hover .gallery-zoom': { opacity: 1 },
                              '&:hover img': { transform: 'scale(1.08)' },
                            }}
                          >
                            <Box
                              component="img" src={item.src}
                              alt={`${ad.businessName} - Photo ${idx + 1}`}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                            />
                            <Box className="gallery-zoom" sx={{
                              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              opacity: 0, transition: 'opacity 0.3s ease',
                            }}>
                              <PhotoLibraryIcon sx={{ color: '#fff', fontSize: 32 }} />
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => { setLightboxIndex(idx); setLightboxType('video'); setLightboxOpen(true); }}
                            sx={{
                              borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                              height: { xs: idx === 0 ? 200 : 140, md: idx === 0 ? 260 : 170 },
                              position: 'relative',
                              '&:hover .video-play': { transform: 'scale(1.15)' },
                              '&:hover img': { transform: 'scale(1.08)' },
                            }}
                          >
                            <Box
                              component="img"
                              src={`https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`}
                              alt={`${ad.businessName} - Video ${idx + 1}`}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                            />
                            <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
                            <Box className="video-play" sx={{
                              position: 'absolute', inset: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'transform 0.3s ease',
                            }}>
                              <Box sx={{
                                width: 56, height: 56, borderRadius: '50%',
                                background: 'rgba(255,0,0,0.9)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                              }}>
                                <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
                              </Box>
                            </Box>
                            <Chip
                              label="VIDEO" size="small"
                              sx={{
                                position: 'absolute', top: 8, left: 8,
                                background: 'rgba(255,0,0,0.85)', color: '#fff',
                                fontWeight: 800, fontSize: 10, height: 22,
                              }}
                            />
                          </Box>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

              {/* Offer highlight card */}
              {ad.offer && (
                <Paper elevation={0} sx={{
                  p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: { xs: 3, md: 4 }, mb: { xs: 2, md: 3 },
                  background: `linear-gradient(135deg, ${catColor}08, ${catColor}15)`,
                  border: `1px solid ${catColor}20`,
                  display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 },
                }}>
                  <Box sx={{
                    width: { xs: 44, md: 56 }, height: { xs: 44, md: 56 }, borderRadius: { xs: 2.5, md: 3 },
                    background: `${catColor}15`, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <LocalOfferIcon sx={{ fontSize: { xs: 22, md: 28 }, color: catColor }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: 11, md: 12 }, fontWeight: 700, color: catColor, textTransform: 'uppercase', letterSpacing: 1, mb: 0.3 }}>
                      Special Offer
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: { xs: '0.95rem', md: '1.15rem' }, wordBreak: 'break-word' }}>
                      {ad.offer}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* WhatsApp Inquiry Card */}
              {ad.whatsapp && (
                <Paper elevation={0} sx={{
                  p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: { xs: 3, md: 4 },
                  background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
                  border: '1px solid rgba(76,175,80,0.2)',
                  display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1.5, md: 2 }, flexDirection: { xs: 'column', sm: 'row' },
                }}>
                  <Box sx={{
                    width: { xs: 44, md: 56 }, height: { xs: 44, md: 56 }, borderRadius: { xs: 2.5, md: 3 },
                    background: '#25D366', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <WhatsAppIcon sx={{ fontSize: { xs: 24, md: 30 }, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: { xs: '0.95rem', md: '1.05rem' }, mb: 0.3 }}>
                      Quick Inquiry via WhatsApp
                    </Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '0.82rem', md: '0.9rem' } }}>
                      Get instant replies — ask about products, pricing, availability, or place orders directly.
                    </Typography>
                  </Box>
                  <Button
                    onClick={handleWhatsApp}
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    fullWidth={false}
                    sx={{
                      background: '#25D366', color: '#fff',
                      borderRadius: 3, textTransform: 'none',
                      fontWeight: 700, fontSize: { xs: '0.88rem', md: '0.95rem' },
                      px: { xs: 2.5, md: 3 }, py: 1.2, flexShrink: 0,
                      width: { xs: '100%', sm: 'auto' },
                      boxShadow: '0 6px 20px rgba(37,211,102,0.3)',
                      '&:hover': { background: '#1ebe5d', boxShadow: '0 8px 28px rgba(37,211,102,0.45)' },
                    }}
                  >
                    Chat Now
                  </Button>
                </Paper>
              )}

              {/* ====== LOCATION & DIRECTIONS — HIGHLIGHTED ====== */}
              {ad.address && (
                <Paper elevation={0} sx={{
                  borderRadius: 4, mt: 3, overflow: 'hidden',
                  border: `2px solid ${catColor}30`,
                  position: 'relative',
                  '&::before': {
                    content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 4,
                    background: `linear-gradient(90deg, ${catColor}, ${catColor}80, ${catColor}40)`,
                  },
                }}>
                  {/* Location Visual */}
                  <Box sx={{
                    width: '100%',
                    height: { xs: 200, sm: 240, md: 260 },
                    position: 'relative',
                    background: `linear-gradient(135deg, ${catColor}12 0%, ${catColor}06 50%, #f8f9fa 100%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    {/* Decorative circles */}
                    <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `${catColor}08`, pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: `${catColor}06`, pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', top: '40%', left: '15%', width: 40, height: 40, borderRadius: '50%', background: `${catColor}05`, pointerEvents: 'none' }} />

                    {/* Location pin icon */}
                    <Box sx={{
                      width: 72, height: 72, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${catColor}, ${catColor}cc)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: `0 8px 32px ${catColor}30`,
                      mb: 2,
                    }}>
                      <LocationOnIcon sx={{ fontSize: 36, color: '#fff' }} />
                    </Box>

                    {/* Address text */}
                    <Typography sx={{
                      fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '1rem', md: '1.1rem' },
                      textAlign: 'center', px: 3, maxWidth: 400, lineHeight: 1.5,
                    }}>
                      {ad.address}
                    </Typography>

                    {/* Quick open link */}
                    <Button
                      size="small"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ad.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        mt: 1.5, textTransform: 'none', fontWeight: 600,
                        fontSize: '0.8rem', color: catColor,
                        '&:hover': { background: `${catColor}10` },
                      }}
                      startIcon={<MapIcon sx={{ fontSize: 16 }} />}
                    >
                      Open in Google Maps
                    </Button>
                  </Box>

                  {/* Location Info & CTAs */}
                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, md: 1.5 }, mb: 2 }}>
                      <Box sx={{
                        width: { xs: 38, md: 44 }, height: { xs: 38, md: 44 }, borderRadius: { xs: 2.5, md: 3 },
                        background: `linear-gradient(135deg, ${catColor}, ${catColor}cc)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        boxShadow: `0 4px 16px ${catColor}30`,
                      }}>
                        <MyLocationIcon sx={{ fontSize: { xs: 18, md: 22 }, color: '#fff' }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: { xs: '0.92rem', md: '1.05rem' }, lineHeight: 1.3 }}>
                          Location & Directions
                        </Typography>
                        <Typography sx={{ color: '#777', fontSize: { xs: '0.78rem', md: '0.85rem' }, mt: 0.2, wordBreak: 'break-word' }}>
                          {ad.address}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: { xs: 1, md: 1.5 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Button
                        variant="contained"
                        startIcon={<DirectionsIcon />}
                        href={ad.mapUrl || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ad.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        fullWidth
                        sx={{
                          py: { xs: 1.1, md: 1.3 }, borderRadius: 3,
                          background: `linear-gradient(135deg, ${catColor}, ${catColor}cc)`,
                          textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.88rem', md: '0.95rem' },
                          boxShadow: `0 6px 20px ${catColor}30`,
                          '&:hover': { boxShadow: `0 10px 28px ${catColor}50` },
                        }}
                      >
                        Get Directions
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<MapIcon />}
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ad.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        fullWidth
                        sx={{
                          py: { xs: 1.1, md: 1.3 }, borderRadius: 3,
                          textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.88rem', md: '0.95rem' },
                          color: catColor, borderColor: `${catColor}40`,
                          '&:hover': { borderColor: catColor, background: `${catColor}08` },
                        }}
                      >
                        View on Map
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Grid>

            {/* Right column: Contact card */}
            <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
              <Box sx={{ position: { md: 'sticky' }, top: { md: 70 } }}>
              <Paper elevation={0} sx={{
                p: { xs: 2.5, sm: 3, md: 3.5 }, borderRadius: { xs: 3, md: 4 },
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.06)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 4, height: 28, background: `linear-gradient(180deg, ${catColor}, ${catColor}80)`, borderRadius: 2 }} />
                  <Typography sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: '1.1rem' }}>
                    Contact Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2.5, borderColor: 'rgba(0,0,0,0.06)' }} />

                {ad.address && (
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: `${catColor}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <LocationOnIcon sx={{ color: catColor, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.3 }}>Address</Typography>
                      <Typography sx={{ color: '#444', fontSize: '0.95rem', lineHeight: 1.6 }}>{ad.address}</Typography>
                      <Typography
                        component="a"
                        href={ad.mapUrl || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ad.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: catColor, fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none', mt: 0.5, display: 'inline-flex', alignItems: 'center', gap: 0.4, '&:hover': { textDecoration: 'underline' } }}
                      >
                        <DirectionsIcon sx={{ fontSize: 14 }} /> Get Directions
                      </Typography>
                    </Box>
                  </Box>
                )}

                {ad.phone && (
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: `${catColor}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PhoneIcon sx={{ color: catColor, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.3 }}>Phone</Typography>
                      <Typography
                        component="a" href={`tel:${ad.phone}`}
                        sx={{ color: catColor, fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {ad.phone}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {ad.email && (
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: `${catColor}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <EmailIcon sx={{ color: catColor, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.3 }}>Email</Typography>
                      <Typography
                        component="a" href={`mailto:${ad.email}`}
                        sx={{ color: catColor, fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {ad.email}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {ad.website && (
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: `${catColor}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <LanguageIcon sx={{ color: catColor, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.3 }}>Website</Typography>
                      <Typography
                        component="a" href={ad.website} target="_blank" rel="noopener noreferrer"
                        sx={{ color: catColor, fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        Visit Website →
                      </Typography>
                    </Box>
                  </Box>
                )}

                {ad.whatsapp && (
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: 'rgba(37,211,102,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <WhatsAppIcon sx={{ color: '#25D366', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.3 }}>WhatsApp</Typography>
                      <Typography
                        component="a" href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); handleWhatsApp(); }}
                        sx={{ color: '#25D366', fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        Send Message →
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* CTA buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
                  {ad.phone && (
                    <Button
                      variant="contained" fullWidth
                      href={`tel:${ad.phone}`}
                      startIcon={<PhoneIcon />}
                      sx={{
                        py: 1.5, background: `linear-gradient(135deg, ${catColor}, ${catColor}cc)`,
                        borderRadius: 3, textTransform: 'none',
                        fontWeight: 700, fontSize: '1rem',
                        boxShadow: `0 8px 24px ${catColor}30`,
                        '&:hover': { boxShadow: `0 12px 32px ${catColor}50` },
                      }}
                    >
                      Call Now
                    </Button>
                  )}
                  {ad.website && (
                    <Button
                      variant="outlined" fullWidth
                      href={ad.website} target="_blank" rel="noopener noreferrer"
                      startIcon={<LanguageIcon />}
                      sx={{
                        py: 1.3, borderRadius: 3, textTransform: 'none',
                        fontWeight: 700, fontSize: '0.95rem',
                        color: catColor, borderColor: `${catColor}40`,
                        '&:hover': { borderColor: catColor, background: `${catColor}08` },
                      }}
                    >
                      Visit Website
                    </Button>
                  )}
                  {ad.whatsapp && (
                    <Button
                      variant="outlined" fullWidth
                      onClick={handleWhatsApp}
                      startIcon={<WhatsAppIcon />}
                      sx={{
                        py: 1.3, borderRadius: 3, textTransform: 'none',
                        fontWeight: 700, fontSize: '0.95rem',
                        color: '#25D366', borderColor: 'rgba(37,211,102,0.4)',
                        '&:hover': { borderColor: '#25D366', background: 'rgba(37,211,102,0.06)' },
                      }}
                    >
                      Chat on WhatsApp
                    </Button>
                  )}
                </Box>
              </Paper>

              {/* ====== SOCIAL MEDIA & FOLLOW ====== */}
              {ad.socialLinks && Object.values(ad.socialLinks).some(Boolean) && (
                <Paper elevation={0} sx={{
                  p: { xs: 2.5, md: 3 }, borderRadius: 4, mt: 2.5,
                  background: '#fff',
                  border: '1px solid rgba(0,0,0,0.06)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 4, height: 24, background: `linear-gradient(180deg, ${catColor}, ${catColor}80)`, borderRadius: 2 }} />
                    <Typography sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: '1rem' }}>
                      Follow on Social Media
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
                    {socialPlatforms.map(({ key, icon, label, color }) => {
                      const url = ad.socialLinks?.[key];
                      if (!url) return null;
                      return (
                        <Button
                          key={key}
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={icon}
                          size="small"
                          sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: 12,
                            color, borderRadius: 2.5,
                            border: `1px solid ${color}25`,
                            background: `${color}08`,
                            px: 1.8, py: 0.8,
                            '&:hover': { background: `${color}15`, borderColor: `${color}50` },
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                </Paper>
              )}

              {/* "Promoted via" small badge */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography sx={{ color: '#bbb', fontSize: 11, fontWeight: 500 }}>
                  Promoted via <span style={{ fontWeight: 700, color: '#999' }}>Kottayil Temple Muchukunnu</span>
                </Typography>
              </Box>
              </Box>{/* end sticky wrapper */}
            </Grid>
          </Grid>

          {/* Disclaimer */}
          <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <Typography sx={{ color: '#bbb', fontSize: 12, fontStyle: 'italic' }}>
              This is a sponsored listing. Kottayil Temple does not endorse or guarantee the products/services of listed businesses.
              For advertising inquiries, contact the temple office.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ====== PROMOTION PAGE FOOTER ====== */}
      <Box sx={{
        background: 'linear-gradient(180deg, #1a3a4a 0%, #122d3a 100%)',
        borderTop: '2px solid rgba(212,175,55,0.3)',
        py: { xs: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 2 }}>
            {/* Business info */}
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 16, mb: 0.3 }}>
                {ad.businessName}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                {ad.tagline}
              </Typography>
              {ad.address && (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, mt: 0.5 }}>
                  {ad.address}
                </Typography>
              )}
            </Box>
            {/* Quick actions */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {ad.phone && (
                <Button
                  size="small" variant="outlined"
                  href={`tel:${ad.phone}`}
                  startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)',
                    textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: 2,
                    '&:hover': { borderColor: 'rgba(255,255,255,0.5)', color: '#fff' },
                  }}
                >
                  Call
                </Button>
              )}
              {ad.whatsapp && (
                <Button
                  size="small" variant="outlined"
                  onClick={handleWhatsApp}
                  startIcon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    color: '#25D366', borderColor: 'rgba(37,211,102,0.3)',
                    textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: 2,
                    '&:hover': { borderColor: '#25D366', background: 'rgba(37,211,102,0.1)' },
                  }}
                >
                  WhatsApp
                </Button>
              )}
              {ad.website && (
                <Button
                  size="small" variant="outlined"
                  href={ad.website} target="_blank" rel="noopener noreferrer"
                  startIcon={<LanguageIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)',
                    textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: 2,
                    '&:hover': { borderColor: 'rgba(255,255,255,0.5)', color: '#fff' },
                  }}
                >
                  Website
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2.5 }} />

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, fontStyle: 'italic' }}>
              Sponsored listing &middot; Kottayil Temple does not endorse or guarantee listed businesses
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>
              &copy; {new Date().getFullYear()} Promoted via kottakovilakam.com
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Lightbox Dialog — Images & Videos */}
      {filteredMedia.length > 0 && (
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              background: 'rgba(0,0,0,0.95)', boxShadow: 'none',
              width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', m: 0, borderRadius: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
            },
          }}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{ position: 'absolute', top: 16, right: 16, color: '#fff', zIndex: 10, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>

          {filteredMedia.length > 1 && (
            <>
              <IconButton
                onClick={() => {
                  setLightboxIndex((prev) => {
                    const next = (prev - 1 + filteredMedia.length) % filteredMedia.length;
                    setLightboxType(filteredMedia[next].type);
                    return next;
                  });
                }}
                sx={{ position: 'absolute', left: 16, color: '#fff', zIndex: 10, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 24 }} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setLightboxIndex((prev) => {
                    const next = (prev + 1) % filteredMedia.length;
                    setLightboxType(filteredMedia[next].type);
                    return next;
                  });
                }}
                sx={{ position: 'absolute', right: 16, color: '#fff', zIndex: 10, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </>
          )}

          {lightboxType === 'image' && filteredMedia[lightboxIndex]?.type === 'image' ? (
            <Box
              component="img"
              src={filteredMedia[lightboxIndex].src}
              alt={`${ad.businessName} gallery ${lightboxIndex + 1}`}
              sx={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 2 }}
            />
          ) : filteredMedia[lightboxIndex]?.videoId ? (
            <Box
              component="iframe"
              src={`https://www.youtube.com/embed/${filteredMedia[lightboxIndex].videoId}?autoplay=1&rel=0`}
              allow="autoplay; encrypted-media"
              allowFullScreen
              sx={{
                width: { xs: '92vw', md: '75vw' },
                height: { xs: '50vw', md: '42vw' },
                maxHeight: '80vh', border: 0, borderRadius: 2,
              }}
              title={`${ad.businessName} video`}
            />
          ) : null}

          <Box sx={{ position: 'absolute', bottom: 20, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              icon={filteredMedia[lightboxIndex]?.type === 'video'
                ? <VideocamIcon sx={{ fontSize: 14, color: '#ff4444 !important' }} />
                : <PhotoLibraryIcon sx={{ fontSize: 14, color: '#ccc !important' }} />
              }
              label={filteredMedia[lightboxIndex]?.type === 'video' ? 'Video' : 'Photo'}
              size="small"
              sx={{ background: 'rgba(255,255,255,0.1)', color: '#ccc', fontWeight: 700, fontSize: 11 }}
            />
            <Typography sx={{ color: '#aaa', fontSize: 14, fontWeight: 600 }}>
              {lightboxIndex + 1} / {filteredMedia.length}
            </Typography>
          </Box>
        </Dialog>
      )}
    </>
  );
}
